import { Button, Card, createListCollection, HStack, Text, VStack } from '@chakra-ui/react';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import { useTranslate } from '@tolgee/react';
import moment from 'moment-timezone';
import { useState } from 'react';
import { DateRange } from '../common/reservation-analytic-service';
import AppointmentChartLayout from './component/appointment-chart-layout';
import AppointmentOverview from './component/appointment-overview-report-card';
import NailCustomerSummaryReportCard from './component/nail-customer-summary-report-card';
import TopServicesAppointmentReportCard from './component/top-services-appointment-report-card';

enum TimePeriodValue {
  THIS_MONTH = 'this_month',
  LAST_30_DAYS = 'last_30_days',
  LAST_7_DAYS = 'last_7_days',
  THIS_WEEK = 'this_week',
  LAST_3_MONTHS = 'last_3_month',
}

const timePeriod = createListCollection({
  items: [
    { label: 'This month', value: TimePeriodValue.THIS_MONTH },
    { label: 'Last 30 days', value: TimePeriodValue.LAST_30_DAYS },
    { label: 'Last 7 days', value: TimePeriodValue.LAST_7_DAYS },
    { label: 'This week', value: TimePeriodValue.THIS_WEEK },
    { label: 'Last 3 months', value: TimePeriodValue.LAST_3_MONTHS },
  ],
});

const getDateRangeForPeriod = (period: TimePeriodValue, timezone: string): DateRange => {
  const timePeriods = {
    [TimePeriodValue.THIS_MONTH]: {
      startDate: moment.tz(timezone).startOf('months').startOf('date').format('YYYY-MM-DD'),
      endDate: moment.tz(timezone).endOf('date').format('YYYY-MM-DD'),
    },
    [TimePeriodValue.LAST_30_DAYS]: {
      startDate: moment.tz(timezone).subtract(29, 'days').startOf('date').format('YYYY-MM-DD'),
      endDate: moment.tz(timezone).endOf('date').format('YYYY-MM-DD'),
    },
    [TimePeriodValue.LAST_7_DAYS]: {
      startDate: moment.tz(timezone).subtract(6, 'days').startOf('date').format('YYYY-MM-DD'),
      endDate: moment.tz(timezone).endOf('date').format('YYYY-MM-DD'),
    },
    [TimePeriodValue.THIS_WEEK]: {
      startDate: moment.tz(timezone).startOf('week').startOf('date').format('YYYY-MM-DD'),
      endDate: moment.tz(timezone).endOf('date').format('YYYY-MM-DD'),
    },
    [TimePeriodValue.LAST_3_MONTHS]: {
      startDate: moment.tz(timezone).subtract(3, 'months').startOf('months').startOf('date').format('YYYY-MM-DD'),
      endDate: moment.tz(timezone).endOf('date').format('YYYY-MM-DD'),
    },
  };

  return timePeriods[period];
};

export default function AppointmentReportDashboard() {
  const timezone = useFocusedCompanyContext().settings.timezone;
  const { t } = useTranslate();

  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriodValue>(TimePeriodValue.THIS_MONTH);
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    getDateRangeForPeriod(TimePeriodValue.THIS_MONTH, timezone),
  );

  const handlePeriodSelect = (period: TimePeriodValue) => {
    const newDateRange = getDateRangeForPeriod(period, timezone);
    setSelectedPeriod(period);
    setDateRange(newDateRange);
  };

  return (
    <VStack align={'stretch'} gapY={'4'}>
      <HStack wrap={'wrap'}>
        {timePeriod.items.map((item) => (
          <Button
            key={item.value}
            onClick={() => {
              handlePeriodSelect(item.value);
            }}
            variant="subtle"
            p={2.5}
            border={'1px solid'}
            fontWeight={'semibold'}
            bgColor={selectedPeriod === item.value ? 'gray.600' : 'white'}
            color={selectedPeriod === item.value ? 'white' : 'black'}
            borderColor={'gray.200'}
            size="sm"
          >
            {t(item.label, item.label)}
          </Button>
        ))}
      </HStack>

      <Card.Root>
        <Card.Header py={0}>
          <Card.Title py={6}>
            <Text fontWeight={'semibold'} fontSize={'2xl'} mb={3}>
              {t('Appointment report dashboard')}
            </Text>
            <Text color={'gray.500'}>
              {t('appointment.analytic.des', 'Monitor your appointment performance and customer insight')}
            </Text>
          </Card.Title>
        </Card.Header>
      </Card.Root>

      <AppointmentOverview dateRange={dateRange} />
      <NailCustomerSummaryReportCard dateRange={dateRange} />
      <TopServicesAppointmentReportCard dateRange={dateRange} />
      <AppointmentChartLayout dateRange={dateRange} />
    </VStack>
  );
}
